import { createFileRoute, Link } from "@tanstack/react-router";
import { LegalLayout } from "@/components/legal-layout";

export const Route = createFileRoute("/terms")({
  component: TermsPage,
});

function TermsPage() {
  return (
    <LegalLayout title="Terms of Service" lastUpdated="December 24, 2025">
      <section className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            1. Acceptance of Terms
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            These Terms of Service ("Terms") constitute a legally binding
            agreement between you and Harshil Agrawal, operating as Images AI
            ("we," "us," or "our") governing your access to and use of the
            Images AI platform and services.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            By creating an account or using our services, you agree to be bound
            by these Terms. If you do not agree, you may not use our services.
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            2. Service Description
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Images AI provides an artificial intelligence-powered platform for
            generating and editing images. Our services include:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>AI-powered image generation from text prompts</li>
            <li>AI-powered image editing and modification</li>
            <li>Cloud storage for generated and edited images</li>
            <li>Credit-based usage system with multiple subscription tiers</li>
            <li>Generation history and management features</li>
          </ul>
          <p className="text-muted-foreground leading-relaxed mt-4">
            All images are generated or edited using third-party AI models
            through Replicate. Content quality, accuracy, and suitability are
            not guaranteed.
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            3. Account Registration
          </h2>

          <h3 className="text-xl font-semibold mb-3 mt-6">3.1 Eligibility</h3>
          <p className="text-muted-foreground leading-relaxed mb-4">
            You must be at least 16 years old to create an account and use our
            services. By creating an account, you represent and warrant that you
            meet this age requirement.
          </p>

          <h3 className="text-xl font-semibold mb-3 mt-6">
            3.2 Account Security
          </h3>
          <p className="text-muted-foreground leading-relaxed mb-4">
            You are responsible for:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>Maintaining the confidentiality of your account credentials</li>
            <li>All activities that occur under your account</li>
            <li>Notifying us immediately of any unauthorized access</li>
          </ul>

          <h3 className="text-xl font-semibold mb-3 mt-6">
            3.3 Accurate Information
          </h3>
          <p className="text-muted-foreground leading-relaxed">
            You agree to provide accurate, current, and complete information
            during registration and to update such information to keep it
            accurate and current.
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">4. Acceptable Use</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            You agree to use our services in compliance with all applicable laws
            and our{" "}
            <Link
              to="/acceptable-use"
              className="text-foreground hover:underline"
            >
              Acceptable Use Policy
            </Link>
            . You agree NOT to:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>
              Generate or edit content that is illegal under German or EU law
            </li>
            <li>
              Create images that infringe on intellectual property rights or
              violate privacy rights
            </li>
            <li>
              Generate harmful, violent, hateful, or discriminatory content
            </li>
            <li>
              Create deepfakes or misleading content without appropriate
              disclosure
            </li>
            <li>Use the service for spam, fraud, or malicious purposes</li>
            <li>Attempt to reverse-engineer or circumvent our systems</li>
            <li>Share your account credentials with others</li>
            <li>Use automated systems to abuse or overload our services</li>
          </ul>
          <p className="text-muted-foreground leading-relaxed mt-4">
            Violation of these terms may result in immediate account suspension
            or termination without refund.
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            5. Intellectual Property Rights
          </h2>

          <h3 className="text-xl font-semibold mb-3 mt-6">5.1 Your Content</h3>
          <p className="text-muted-foreground leading-relaxed mb-4">
            <strong>You retain full ownership</strong> of all images you
            generate or edit using our platform. We claim no ownership rights
            over your generated content.
          </p>

          <h3 className="text-xl font-semibold mb-3 mt-6">5.2 License to Us</h3>
          <p className="text-muted-foreground leading-relaxed mb-4">
            By using our services, you grant us a limited, non-exclusive,
            worldwide license to:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>
              Store and display your generated images to you within the service
            </li>
            <li>
              Process your prompts and images through our AI providers
              (Replicate)
            </li>
            <li>
              Use anonymized, aggregated data to improve our services (no
              personal data)
            </li>
          </ul>
          <p className="text-muted-foreground leading-relaxed mt-4">
            This license terminates when you delete your content or account,
            subject to reasonable backup retention periods (maximum 30 days).
          </p>

          <h3 className="text-xl font-semibold mb-3 mt-6">5.3 Our Platform</h3>
          <p className="text-muted-foreground leading-relaxed">
            The Images AI platform, including all software, designs, text,
            graphics, and other content (excluding your generated images), is
            owned by us or our licensors and is protected by copyright,
            trademark, and other intellectual property laws.
          </p>

          <h3 className="text-xl font-semibold mb-3 mt-6">
            5.4 Third-Party AI Models
          </h3>
          <p className="text-muted-foreground leading-relaxed">
            The AI models used for image generation and editing are provided by
            third-party AI services (Replicate) and their respective creators.
            You agree to comply with any applicable licenses or usage terms for
            these models.
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            6. Subscription Plans and Payment
          </h2>

          <h3 className="text-xl font-semibold mb-3 mt-6">
            6.1 Subscription Tiers
          </h3>
          <p className="text-muted-foreground leading-relaxed mb-4">
            We offer three subscription tiers with monthly credit allowances:
          </p>
          <div className="bg-muted/50 border border-border rounded-2xl p-6 space-y-3">
            <p>
              <strong>Free:</strong> 5 credits per month (no payment required)
            </p>
            <p>
              <strong>Pro:</strong> 40 credits per month (paid subscription)
            </p>
            <p>
              <strong>Premium:</strong> 120 credits per month (paid
              subscription)
            </p>
          </div>
          <p className="text-muted-foreground leading-relaxed mt-4">
            Pricing varies by region (US, EU, India). Current pricing is
            displayed on our website and during the checkout process.
          </p>

          <h3 className="text-xl font-semibold mb-3 mt-6">
            6.2 Payment Processing
          </h3>
          <p className="text-muted-foreground leading-relaxed mb-4">
            All payments are processed securely through Stripe. We do not store
            your payment card details on our servers. By subscribing, you
            authorize Stripe to charge your payment method for the subscription
            fee.
          </p>

          <h3 className="text-xl font-semibold mb-3 mt-6">
            6.3 Subscription Renewal
          </h3>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Paid subscriptions automatically renew on a monthly basis unless you
            cancel before the renewal date. You will be charged the then-current
            subscription rate. Credits reset at the beginning of each billing
            period and do not roll over.
          </p>

          <h3 className="text-xl font-semibold mb-3 mt-6">6.4 VAT and Taxes</h3>
          <p className="text-muted-foreground leading-relaxed">
            Prices may be subject to Value Added Tax (VAT) or other applicable
            taxes based on your location. VAT will be calculated and displayed
            during checkout.
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-2xl p-6">
          <h2 className="text-2xl font-semibold mb-4">
            7. Right of Withdrawal (Widerrufsrecht)
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            <strong>EU Consumer Protection:</strong> If you are a consumer in
            the European Union, you have the right to withdraw from your
            subscription within 14 days of purchase without giving any reason.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-4">
            <strong>Exception:</strong> By using credits from a paid
            subscription, you expressly request that we begin service delivery
            immediately and acknowledge that you lose your right of withdrawal
            once you have consumed credits.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            To exercise your right of withdrawal (if applicable), contact us at{" "}
            <a
              href="mailto:hey@harshil.dev"
              className="text-foreground hover:underline"
            >
              hey@harshil.dev
            </a>
            .
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            8. Cancellation and Refunds
          </h2>

          <h3 className="text-xl font-semibold mb-3 mt-6">8.1 Cancellation</h3>
          <p className="text-muted-foreground leading-relaxed mb-4">
            You may cancel your subscription at any time through your account
            settings or by contacting support. Cancellation takes effect at the
            end of the current billing period. You will retain access to paid
            features until that date.
          </p>

          <h3 className="text-xl font-semibold mb-3 mt-6">8.2 Refund Policy</h3>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Subscription fees are generally non-refundable except:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>
              Within the 14-day withdrawal period (if no credits consumed)
            </li>
            <li>
              In case of service unavailability or technical issues (pro-rated
              refund)
            </li>
            <li>If required by applicable consumer protection laws</li>
          </ul>
          <p className="text-muted-foreground leading-relaxed mt-4">
            Unused credits are not refundable and do not carry monetary value.
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            9. Service Availability and Modifications
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            We strive to provide continuous service availability but do not
            guarantee uninterrupted access. We reserve the right to:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>Modify, suspend, or discontinue any aspect of the service</li>
            <li>Change pricing with 30 days' notice to existing subscribers</li>
            <li>Update AI models and features</li>
            <li>Perform maintenance and updates</li>
          </ul>
          <p className="text-muted-foreground leading-relaxed mt-4">
            Significant changes affecting paid subscribers will be communicated
            via email with reasonable advance notice.
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            10. Account Termination
          </h2>

          <h3 className="text-xl font-semibold mb-3 mt-6">
            10.1 Termination by You
          </h3>
          <p className="text-muted-foreground leading-relaxed mb-4">
            You may terminate your account at any time through account settings.
            Upon termination, your data will be deleted within 30 days, except
            where retention is required by law.
          </p>

          <h3 className="text-xl font-semibold mb-3 mt-6">
            10.2 Termination by Us
          </h3>
          <p className="text-muted-foreground leading-relaxed mb-4">
            We reserve the right to suspend or terminate your account if you:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>Violate these Terms or our Acceptable Use Policy</li>
            <li>Engage in fraudulent or illegal activity</li>
            <li>Fail to pay subscription fees</li>
            <li>Create risk or legal liability for us or other users</li>
          </ul>
          <p className="text-muted-foreground leading-relaxed mt-4">
            We will provide notice before termination unless immediate action is
            required for legal or security reasons. No refunds will be provided
            for terminations due to Terms violations.
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            11. Limitation of Liability
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            <strong>German Law Provisions:</strong> Under German law, our
            liability is limited as follows:
          </p>

          <h3 className="text-xl font-semibold mb-3 mt-6">
            11.1 Unlimited Liability
          </h3>
          <p className="text-muted-foreground leading-relaxed mb-4">
            We are liable without limitation for:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>Intentional acts or gross negligence</li>
            <li>Personal injury, death, or health damage</li>
            <li>Violations of guaranteed characteristics</li>
            <li>Claims under the German Product Liability Act</li>
          </ul>

          <h3 className="text-xl font-semibold mb-3 mt-6">
            11.2 Limited Liability
          </h3>
          <p className="text-muted-foreground leading-relaxed mb-4">
            For simple negligence, we are only liable for:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>
              Breach of essential contractual obligations (material duties)
            </li>
            <li>
              Damages limited to foreseeable, typical losses at the time of
              contract conclusion
            </li>
          </ul>

          <h3 className="text-xl font-semibold mb-3 mt-6">11.3 Exclusion</h3>
          <p className="text-muted-foreground leading-relaxed">
            All other liability is excluded to the extent permitted by law. This
            includes liability for indirect, consequential, or incidental
            damages, loss of profits, or loss of data beyond the limitations
            above.
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">12. Indemnification</h2>
          <p className="text-muted-foreground leading-relaxed">
            You agree to indemnify and hold harmless Harshil Agrawal (Images AI)
            from any claims, damages, or expenses arising from your use of the
            service, your generated content, or your violation of these Terms or
            applicable law.
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            13. Warranties and Disclaimers
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Our services are provided "as is" and "as available." We do not
            warrant that:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>The service will be uninterrupted or error-free</li>
            <li>
              AI-generated content will meet your expectations or be accurate
            </li>
            <li>All features will be available at all times</li>
            <li>Third-party AI models will perform as expected</li>
          </ul>
          <p className="text-muted-foreground leading-relaxed mt-4">
            You are solely responsible for evaluating the suitability and
            accuracy of AI-generated content for your intended use.
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            14. Governing Law and Jurisdiction
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            These Terms are governed by the laws of the Federal Republic of
            Germany, excluding the UN Convention on Contracts for the
            International Sale of Goods (CISG).
          </p>
          <p className="text-muted-foreground leading-relaxed">
            For disputes with merchants, legal entities under public law, or
            special funds under public law, the exclusive place of jurisdiction
            is Berlin, Germany. For consumers, statutory provisions regarding
            jurisdiction apply.
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            15. Dispute Resolution
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            EU consumers may use the European Commission's Online Dispute
            Resolution platform:{" "}
            <a
              href="https://ec.europa.eu/consumers/odr"
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground hover:underline"
            >
              https://ec.europa.eu/consumers/odr
            </a>
          </p>
          <p className="text-muted-foreground leading-relaxed">
            We are not obligated to participate in dispute resolution
            proceedings before consumer arbitration boards but may do so
            voluntarily.
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">16. Changes to Terms</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            We may update these Terms from time to time. We will notify you of
            material changes by email or through a prominent notice on our
            website at least 30 days before the changes take effect.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            Continued use of our services after changes take effect constitutes
            acceptance of the updated Terms. If you do not agree, you should
            cancel your subscription before the effective date.
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">17. Severability</h2>
          <p className="text-muted-foreground leading-relaxed">
            If any provision of these Terms is found to be invalid or
            unenforceable, the remaining provisions will continue in full force
            and effect. The invalid provision will be replaced with a valid
            provision that most closely reflects the original intent.
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">18. Entire Agreement</h2>
          <p className="text-muted-foreground leading-relaxed">
            These Terms, together with our{" "}
            <Link to="/privacy" className="text-foreground hover:underline">
              Privacy Policy
            </Link>
            ,{" "}
            <Link
              to="/acceptable-use"
              className="text-foreground hover:underline"
            >
              Acceptable Use Policy
            </Link>
            , and{" "}
            <Link to="/cookies" className="text-foreground hover:underline">
              Cookie Policy
            </Link>
            , constitute the entire agreement between you and us regarding the
            use of our services.
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            19. Contact Information
          </h2>
          <div className="bg-muted/50 border border-border rounded-2xl p-6">
            <p className="mb-4">
              For questions about these Terms, please contact us:
            </p>
            <p>
              <strong>Email:</strong>{" "}
              <a
                href="mailto:hey@harshil.dev"
                className="text-foreground hover:underline"
              >
                hey@harshil.dev
              </a>
            </p>
            <p className="mt-4">
              <strong>Postal Address:</strong>
            </p>
            <p>Harshil Agrawal</p>
            <p>Helene-Jacobs-Str.</p>
            <p>14199 Berlin, Germany</p>
          </div>
        </div>
      </section>
    </LegalLayout>
  );
}
